import React, { useCallback, useEffect, useMemo, useState } from "react";
import DropDownMenu from "../../../common/components/DropDownMenu";
import TextField from "../../../common/components/TextField";

import { useFormik } from "formik";

import styles from "../ProjectContainer.module.scss";
import { useTranslation } from "react-i18next";
import TeamService from "../../../service/TeamService";

import { useCart } from "../../../utils/CartHooks";
import { useProjectFetcher } from "../../../utils/ProjectsFetcherHooks";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../utils/Constant";
import { routeFormatter } from "../../../utils/Formatter";
import DeleteModal from "../../../components/DeleteModal";
import { useTeamFetcher } from "../../../utils/TeamsFetcherHooks";
import { useCurrency } from "../../../utils/Currencies";
import { toast } from "react-toastify";

const TeamTopSection = (props) => {
  const { canDeleteTeam, canRenameTeam, team = {} } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [editMode, setEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { fetchCartItemsAndUpdateStore } = useCart();
  const { fetchMyProjectsAndUpdateStore } = useProjectFetcher();
  const { fetchMyTeamsAndUpdateStore } = useTeamFetcher();
  const { getCurrency } = useCurrency();

  const onDeleteClickClose = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowDeleteModal(false);
  };

  const onDelete = useCallback(async () => {
    try {
      await TeamService.deleteTeam({ id: team.id });
      //When a team is deleted, Route to home page
      navigate(routeFormatter(ROUTES.HOME));

      await fetchMyTeamsAndUpdateStore();
      await fetchMyProjectsAndUpdateStore();
      await fetchCartItemsAndUpdateStore(getCurrency()); //Because deleting a Project and it's designs can impact the cart
    } catch (e) {
      console.error(e);
      toast("Internal Issue deleting team, error is '" + String(e) + "'");
    } finally {
      setShowDeleteModal(false);
    }
  }, [
    team,
    fetchCartItemsAndUpdateStore,
    fetchMyProjectsAndUpdateStore,
    fetchMyTeamsAndUpdateStore,
    navigate,
    getCurrency,
  ]);

  const formik = useFormik({
    initialValues: {
      name: team.name,
    },
    onSubmit: async (values) => {
      const res = await TeamService.renameTeam({
        id: team.id,
        name: values.name,
      });

      if (res.status === 200) {
        setEditMode(false);
        fetchMyTeamsAndUpdateStore();
      } else {
        formik.setFieldError("name", t("team.error_occured"));
      }
    },
  });

  useEffect(() => {
    formik.setFieldValue("name", team.name);

    // If we add formik to the dependencies, it creates a circular dependancy
    // eslint-disable-next-line
  }, [team.name]);

  const options = useMemo(
    () => [
      {
        labelKey: "team.edit_team_name",
        icon: "edit",
        disabled: !canRenameTeam,
        onClick: () => setEditMode(true),
      },
      {
        labelKey: "team.delete_menu",
        icon: "delete",
        disabled: !canDeleteTeam,
        onClick: () => setShowDeleteModal(true),
      },
    ],
    [canDeleteTeam, canRenameTeam]
  );

  const cancelEdit = useCallback(() => {
    setEditMode(false);
    formik.setFieldValue("name", team.name);
    // if we add formik to the dependencies, it c
    // eslint-disable-next-line
  }, [formik, team.name]);

  if (editMode) {
    return (
      <form onSubmit={formik.handleSubmit}>
        <div className={styles.titleContainer}>
          <TextField formik={formik} namespace="name" />
          {/* TODO: Change styling when we have a design; it shouldn't use bootstrap classes */}
          <button type="submit" className={styles.saveButton}>
            {t("general.save")}
          </button>
          {/* TODO: Change styling when we have a design; it shouldn't use bootstrap classes */}
          <button onClick={cancelEdit} className={styles.cancelButton}>
            {t("general.cancel")}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className={styles.titleContainer}>
      <h4 className={styles.title}>{team.name}</h4>
      <DropDownMenu menuIcon="ExpandMore" options={options} />
      <DeleteModal
        open={showDeleteModal}
        onDelete={onDelete}
        title={t("team.delete_modal")}
        onClickClose={onDeleteClickClose}
      />
    </div>
  );
};

export default TeamTopSection;
